import { showNotification } from '../../utils.js';
import CompanyService from '../../services/company.js';
import WhatsAppService from '../../services/whatsapp.js';
import { renderCompanyCard } from './components/CompanyCard.js';
import { setupCompanyModal } from './modals/CompanyModal.js';
import { setupPaymentModal } from './modals/PaymentModal.js';
import { setupWhatsAppModal } from './modals/WhatsAppModal.js';

export default class CompaniesView {
    constructor() {
        this.companies = [];
        this.mainContent = document.getElementById('main-content');
        this.isLoading = true;
        // Bind the event handler for delegation.
        this.handleClick = this.handleClick.bind(this);
        this.eventsAttached = false;
    }

    async mount() {
        try {
            // Clean up URL if it contains statistics or templates.
            if (
                window.location.hash.includes('/statistics') ||
                window.location.hash.includes('/templates')
            ) {
                window.location.hash = '#/companies';
                return; // The route handler will remount this view with a clean URL.
            }
            
            // Show loading state immediately.
            this.isLoading = true;
            this.render();
            
            await this.loadCompanies();
            
            // Check WhatsApp status for all companies with a non-empty phone number.
            for (const company of this.companies) {
                if (company.whatsapp_phone && company.whatsapp_phone.trim() !== '') {
                    try {
                        const status = await WhatsAppService.getStatus(company.whatsapp_phone);
                        company.whatsapp_status = status.status;
                    } catch (error) {
                        console.error('Error fetching WhatsApp status:', error);
                        company.whatsapp_status = 'disconnected';
                    }
                }
            }
            
            this.render();
        } catch (error) {
            showNotification(error.message, 'error');
        } finally {
            this.isLoading = false;
            this.render();
            this.attachEventListeners();
        }
    }

    async loadCompanies() {
        this.companies = await CompanyService.getCompanies();
    }

    /**
     * Refreshes the companies list while showing a loading state.
     */
    async refreshCompanies() {
        this.isLoading = true;
        this.render();
        await this.loadCompanies();
        this.isLoading = false;
        this.render();
    }

    render() {
        const template = `
            <div class="flex justify-between items-center mb-6">
                <h2 class="text-2xl font-bold text-gray-900">Мои магазины</h2>
                <button id="add-company-btn" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                    Добавить компанию
                </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                ${this.isLoading 
                    ? Array(3).fill().map(() => renderCompanyCard({ loading: true })).join('')
                    : this.companies.map(company => renderCompanyCard(company)).join('')
                }
            </div>
        `;
        this.mainContent.innerHTML = template;
    }

    /**
     * Delegated event handler to catch all click events in the main content.
     */
    handleClick(event) {
        const button = event.target.closest('button');
        if (!button) return;
        
        // Handle "Add Company" button.
        if (button.id === 'add-company-btn') {
            setupCompanyModal(null, async () => {
                await this.refreshCompanies();
            });
            return;
        }
        
        // All other actions require a company context.
        const companyCard = button.closest('[data-company-id]');
        if (!companyCard) return;
        const companyId = companyCard.dataset.companyId;
        const parsedCompanyId = parseInt(companyId, 10);

        // Edit company.
        if (button.classList.contains('edit-company-btn')) {
            const company = this.companies.find(c => c.id === parsedCompanyId);
            setupCompanyModal(company, async () => {
                await this.refreshCompanies();
            });
            return;
        }

        // Delete company.
        if (button.classList.contains('delete-company-btn')) {
            if (confirm('Вы уверены, что хотите удалить эту компанию?')) {
                (async () => {
                    try {
                        await CompanyService.deleteCompany(companyId);
                        showNotification('Компания успешно удалена');
                        await this.refreshCompanies();
                    } catch (error) {
                        showNotification(error.message, 'error');
                    }
                })();
            }
            return;
        }

        // Manage templates.
        if (button.classList.contains('manage-templates-btn')) {
            this.isLoading = true;
            this.render();
            const hash = `#/companies/${companyId}/templates`;
            if (window.location.hash === hash) {
                window.app.handleRoute();
            } else {
                window.location.hash = hash;
            }
            return;
        }

        // Manage payment.
        if (button.classList.contains('manage-payment-btn')) {
            const company = this.companies.find(c => c.id === parsedCompanyId);
            setupPaymentModal(company, async () => {
                await this.refreshCompanies();
            });
            return;
        }

        // View statistics.
        if (button.classList.contains('view-statistics-btn')) {
            this.isLoading = true;
            this.render();
            const hash = `#/companies/${companyId}/statistics`;
            if (window.location.hash === hash) {
                window.app.handleRoute();
            } else {
                window.location.hash = hash;
            }
            return;
        }

        // Manage WhatsApp.
        if (button.classList.contains('manage-whatsapp-btn')) {
            const company = this.companies.find(c => c.id === parsedCompanyId);
            setupWhatsAppModal(company, async () => {
                await this.refreshCompanies();
            });
            return;
        }
    }

    /**
     * Attaches a single delegated event listener to the main content.
     */
    attachEventListeners() {
        if (!this.eventsAttached) {
            this.mainContent.addEventListener('click', this.handleClick);
            this.eventsAttached = true;
        }
    }

    /**
     * Cleanup when view is unmounted.
     */
    unmount() {
        if (this.eventsAttached) {
            this.mainContent.removeEventListener('click', this.handleClick);
            this.eventsAttached = false;
        }
    }
}
